import { Text } from '@chakra-ui/react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import React from 'react';

import { useBodyTypesFilter } from '@/hooks/filters/body-types-filter.hook';
import { useBrandsRangesFilter } from '@/hooks/filters/brands-ranges-filter.hook';
import { useColorsFilter } from '@/hooks/filters/colors-filter.hook';
import { useDoorsFilter } from '@/hooks/filters/doors-filter.hook';
import { useDrivetrainFilter } from '@/hooks/filters/drivetrain-filter.hook';
import { useFeaturesFilter } from '@/hooks/filters/features-filter.hook';
import { useFinanceFilter } from '@/hooks/filters/finance-filter.hook';
import { useFuelConsumptionFilter } from '@/hooks/filters/fuel-consumption.hook';
import { useFuelTypeFilter } from '@/hooks/filters/fuel-type-filter.hook';
import { useMileageFilter } from '@/hooks/filters/mileage-filter.hook';
import { usePriceFilter } from '@/hooks/filters/price-filter.hook';
import { useTransmissionTypeFilter } from '@/hooks/filters/transmission-type-filter.hook';
import { useYearsFilterHook } from '@/hooks/filters/years-filter.hook';
import { useRoutesConfig } from '@/hooks/routes-config';
import { useFiltersQuery } from '@/hooks/used-cars/filters-query.hook';

import { FilterTag } from './components/filter-tag/filter-tag';
import styles from './filter-tag-list.module.scss';

export const FilterTagList: React.FC<{ noTags?: boolean }> = ({ noTags }) => {
  const { filtersQuery, setIsCleared } = useFiltersQuery();
  const router = useRouter();
  const query = useSearchParams();
  const pathname = usePathname();
  const routers = useRoutesConfig();

  const { resetPriceFilter, hasSelectedPriceFilter, selectedPriceFilterLabel } =
    usePriceFilter();

  const { resetYearsFilter, selectedYearsFilterLabel, hasSelectedYearsFilter } =
    useYearsFilterHook();

  const {
    selectedMileageFilterLabel,
    resetMileageFilter,
    hasSelectedMileageFilter,
  } = useMileageFilter();

  const { selectedFinanceFilter, setSelectedFinanceFilter } =
    useFinanceFilter();

  const {
    brandsRangesSelectedFilters,
    hasSelectedBrandsRanges,
    resetBrandsRanges,
    toggleBrandRange,
  } = useBrandsRangesFilter();

  const {
    selectedColorFilters,
    resetSelectedColorFilter,
    hasSelectedColorFilters,
  } = useColorsFilter();

  const {
    bodyTypesSelectedFilters,
    resetSelectedBodyTypes,
    hasSelectedBodyTypes,
  } = useBodyTypesFilter();

  const {
    selectedDoorsFilter,
    resetSelectedDoorsFilter,
    hasSelectedDoorsFilter,
  } = useDoorsFilter();

  const {
    hasSelectedTransmissionFilter,
    selectedTransmissionTypes,
    resetSelectedTransmissionTypes,
  } = useTransmissionTypeFilter();

  const {
    selectedFuelTypes,
    resetSelectedFuelTypes,
    hasSelectedFuelTypesFilter,
  } = useFuelTypeFilter();

  const {
    selectedDrivetrainTypes,
    resetSelectedDrivetrainTypes,
    hasSelectedDrivetrainFilter,
  } = useDrivetrainFilter();

  const { selectedFeatures, resetSelectedFeatures, hasSelectedFeaturesFilter } =
    useFeaturesFilter();

  const {
    resetConsumptionFilter,
    selectedConsumptionFilterLabel,
    hasSelectedConsumptionFilter,
  } = useFuelConsumptionFilter();

  const clearAll = async () => {
    resetPriceFilter();
    resetFinanceFilter();
    resetYearsFilter();
    resetMileageFilter();
    resetConsumptionFilter();

    resetBrandsRanges(null);
    resetSelectedBodyTypes(null);
    resetSelectedColorFilter(null);
    resetSelectedDoorsFilter(null);
    resetSelectedTransmissionTypes(null);
    resetSelectedFuelTypes(null);
    resetSelectedDrivetrainTypes(null);
    resetSelectedFeatures(null);

    setIsCleared(true);

    const id = query.get('id');
    const bodyType = query.get('bodyType');

    if (
      id &&
      bodyType &&
      pathname.indexOf(`${routers.usedCarsDealerships.path}/`) !== -1
    ) {
      router.push(`${routers.usedCarsDealerships.path}/${id}`);
      return;
    }

    await router.push(pathname?.split('?')[0] as string);
  };

  const resetFinanceFilter = () => {
    setSelectedFinanceFilter('');
  };

  const clearSelectedBodyTypes = (bodyId: number) => {
    resetSelectedBodyTypes(bodyId);

    const id = query.get('id');
    const bodyType = query.get('bodyType');

    if (
      id &&
      bodyType &&
      pathname.indexOf(`${routers.usedCarsDealerships.path}/`) !== -1
    ) {
      router.push(`${routers.usedCarsDealerships.path}/${id}`);
    }
  };

  // TODO: has*
  const isFilterListVisible =
    hasSelectedBrandsRanges ||
    hasSelectedFeaturesFilter ||
    hasSelectedBodyTypes ||
    hasSelectedColorFilters ||
    hasSelectedDoorsFilter ||
    hasSelectedTransmissionFilter ||
    hasSelectedFuelTypesFilter ||
    hasSelectedDrivetrainFilter ||
    selectedFinanceFilter ||
    hasSelectedYearsFilter ||
    hasSelectedMileageFilter ||
    hasSelectedPriceFilter ||
    hasSelectedConsumptionFilter;

  if (!isFilterListVisible) {
    return null;
  }

  if (noTags)
    return (
      <div className={styles.filterTagListWrapper}>
        <div className={styles.indicator}>
          <Text className={styles.count}>
            {String(Object.keys(filtersQuery).length)}
          </Text>
        </div>
      </div>
    );

  return (
    <div className={styles.filterTagListWrapper}>
      {brandsRangesSelectedFilters.map((option) => (
        <FilterTag
          key={option.id}
          option={option}
          handleRemove={() => toggleBrandRange(option)}
        />
      ))}
      {hasSelectedPriceFilter && (
        <FilterTag
          option={{ fullName: selectedPriceFilterLabel }}
          handleRemove={resetPriceFilter}
        />
      )}
      {hasSelectedYearsFilter && (
        <FilterTag
          option={{ fullName: selectedYearsFilterLabel }}
          handleRemove={resetYearsFilter}
        />
      )}
      {selectedFinanceFilter && (
        <FilterTag
          option={{ fullName: selectedFinanceFilter }}
          handleRemove={resetFinanceFilter}
        />
      )}
      {hasSelectedMileageFilter && (
        <FilterTag
          option={{ fullName: selectedMileageFilterLabel }}
          handleRemove={resetMileageFilter}
        />
      )}
      {bodyTypesSelectedFilters.map((option) => (
        <FilterTag
          key={option.id}
          option={option}
          handleRemove={() => clearSelectedBodyTypes(option.id)}
        />
      ))}
      {selectedColorFilters.map((option) => (
        <FilterTag
          key={option.id}
          option={option}
          handleRemove={() => resetSelectedColorFilter(option.id)}
        />
      ))}
      {selectedDoorsFilter.map((option) => (
        <FilterTag
          key={option.id}
          option={option}
          handleRemove={() => resetSelectedDoorsFilter(option.id)}
        />
      ))}
      {selectedTransmissionTypes.map((option) => (
        <FilterTag
          key={option.id}
          option={option}
          handleRemove={() => resetSelectedTransmissionTypes(option.id)}
        />
      ))}
      {selectedFuelTypes.map((option) => (
        <FilterTag
          key={option.id}
          option={option}
          handleRemove={() => resetSelectedFuelTypes(option.id)}
        />
      ))}
      {selectedDrivetrainTypes.map((option) => (
        <FilterTag
          key={option.id}
          option={option}
          handleRemove={() => resetSelectedDrivetrainTypes(option.id)}
        />
      ))}
      {selectedFeatures.map((option) => (
        <FilterTag
          key={option.id}
          option={option}
          handleRemove={() => resetSelectedFeatures(option.id)}
        />
      ))}
      {hasSelectedConsumptionFilter && (
        <FilterTag
          option={{ fullName: selectedConsumptionFilterLabel }}
          handleRemove={resetConsumptionFilter}
        />
      )}
      <span className={styles.filterTagListClearAll} onClick={clearAll}>
        Clear Filters
      </span>
    </div>
  );
};
